import { supabase } from '@/lib/supabase/client';
import type { ShipmentItem, ShipmentItemInsert, ShipmentItemUpdate } from '@/types/shipment-items';

export const shipmentItemsService = {
  async getByShipmentId(shipmentId: string, strict = false): Promise<ShipmentItem[]> {
    try {
      const rows: ShipmentItem[] = [];
      for (let start = 0; ; start += 1000) {
        const { data, error } = await supabase.from('shipment_items').select('*').eq('shipment_id', shipmentId).order('created_at').order('id').range(start, start + 999);
        if (error) throw error;
        rows.push(...(data as ShipmentItem[] || []));
        if ((data?.length || 0) < 1000) return rows;
      }
    } catch (error) {
      if (strict) throw error;
      console.error('Error fetching shipment items:', error);
      return [];
    }
  },

  async getByShipmentIds(shipmentIds: string[], strict = false): Promise<ShipmentItem[]> {
    if (shipmentIds.length === 0) return [];

    try {
      const rows: ShipmentItem[] = [];
      for (let start = 0; ; start += 1000) {
        const { data, error } = await supabase.from('shipment_items').select('*').in('shipment_id', shipmentIds).order('created_at').order('id').range(start, start + 999);
        if (error) throw error;
        rows.push(...(data as ShipmentItem[] || []));
        if ((data?.length || 0) < 1000) return rows;
      }
    } catch (error) {
      if (strict) throw error;
      console.error('Error fetching shipment items:', error);
      return [];
    }
  },

  async getById(id: string): Promise<ShipmentItem | null> {
    try {
      const { data, error } = await supabase
        .from('shipment_items')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching shipment item:', error);
        return null;
      }

      return (data as ShipmentItem) || null;
    } catch (error) {
      console.error('Error fetching shipment item:', error);
      return null;
    }
  },

  async create(data: ShipmentItemInsert): Promise<ShipmentItem> {
    try {
      const insertData = {
        shipment_id: data.shipment_id,
        component_id: data.component_id ?? null,
        item_description: data.item_description,
        quantity: data.quantity,
        tracking_number: data.tracking_number ?? null,
        carrier: data.carrier ?? null,
        estimated_delivery_date: data.estimated_delivery_date ?? null,
      };

      const { data: newItem, error } = await supabase
        .from('shipment_items')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Error creating shipment item:', error);
        throw new Error(`Failed to create shipment item: ${error.message}`);
      }

      return newItem as ShipmentItem;
    } catch (error) {
      console.error('Error creating shipment item:', error);
      throw error;
    }
  },

  async updateDeliveryDetails(
    original: ShipmentItem,
    details: Pick<ShipmentItem, 'carrier' | 'tracking_number' | 'estimated_delivery_date'>,
  ): Promise<ShipmentItem> {
    const { data, error } = await supabase
      .from('shipment_items')
      .update({
        carrier: details.carrier,
        tracking_number: details.tracking_number,
        estimated_delivery_date: details.estimated_delivery_date,
      })
      .eq('id', original.id)
      .eq('shipment_id', original.shipment_id)
      .eq('updated_at', original.updated_at)
      .select()
      .maybeSingle();

    if (error) throw new Error(`Failed to update tracking: ${error.message}`);
    if (!data) throw new Error('This shipment item changed. Reload before updating its tracking.');
    return data as ShipmentItem;
  },

  async update(id: string, data: ShipmentItemUpdate): Promise<ShipmentItem> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.component_id !== undefined) updateData.component_id = data.component_id;
      if (data.item_description !== undefined) updateData.item_description = data.item_description;
      if (data.quantity !== undefined) updateData.quantity = data.quantity;
      if (data.tracking_number !== undefined) updateData.tracking_number = data.tracking_number;
      if (data.carrier !== undefined) updateData.carrier = data.carrier;
      if (data.estimated_delivery_date !== undefined) updateData.estimated_delivery_date = data.estimated_delivery_date;

      const { data: updatedItem, error } = await supabase
        .from('shipment_items')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating shipment item:', error);
        throw new Error(`Failed to update shipment item: ${error.message}`);
      }

      if (!updatedItem) {
        throw new Error('Shipment item not found');
      }

      return updatedItem as ShipmentItem;
    } catch (error) {
      console.error('Error updating shipment item:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('shipment_items').delete().eq('id', id);

      if (error) {
        console.error('Error deleting shipment item:', error);
        throw new Error(`Failed to delete shipment item: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting shipment item:', error);
      throw error;
    }
  },

  async deleteByShipmentId(shipmentId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('shipment_items')
        .delete()
        .eq('shipment_id', shipmentId);

      if (error) {
        console.error('Error deleting shipment items:', error);
        throw new Error(`Failed to delete shipment items: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting shipment items:', error);
      throw error;
    }
  },
};
